/**
 * anonTokenBackup.ts
 * Export and import anonymous edit tokens as plain .txt files.
 * 
 * File format:
 * # Societea Anon Keys v1
 * # origin: societea.app
 * # generated_at: 2025-10-10T14:33:21Z
 * impact\t12345\t<token>
 * comment\t98765\t<token>
 * 
 * Simple, portable, no metadata by default (privacy-first).
 */

import { loadAnonToken, saveAnonToken, AnonEntity } from "./anonTokenVault";

/**
 * Reference to an anonymous entity key
 */
type KeyRef = {
  type: AnonEntity;
  id: number | string;
  createdAt?: string;
};

/**
 * Export a single anonymous entity key to .txt file
 * @param type - Entity type
 * @param id - Entity ID
 */
export async function downloadSingleKey(
  type: AnonEntity,
  id: number | string
): Promise<void> {
  const token = await loadAnonToken(type, id);
  if (!token) {
    throw new Error("No token found for this entity");
  }

  const timestamp = Date.now();
  const lines = [
    "# Societea Anon Keys v2",
    `# origin: ${window.location.hostname}`,
    `# generated_at: ${new Date().toISOString()}`,
    `# entity: ${type}:${id}`,
    "",
    `${type}\t${id}\t${token}\t${timestamp}`,
  ];

  const blob = new Blob([lines.join("\n") + "\n"], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const date = new Date().toISOString().replace(/[:.]/g, "-").split("T")[0];
  a.href = url;
  a.download = `societea_anon_key_${type}_${id}_${date}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Export all anonymous keys from the provided list
 * @param known - List of known anonymous entity references
 */
export async function exportAllKeys(known: KeyRef[]): Promise<number> {
  const lines = [
    "# Societea Anon Keys v2",
    `# origin: ${window.location.hostname}`,
    `# generated_at: ${new Date().toISOString()}`,
    `# total_entries: ${known.length}`,
    `# format: type<TAB>id<TAB>token<TAB>timestamp_ms`,
    "",
  ];

  let exported = 0;
  const currentTime = Date.now();

  for (const ref of known) {
    try {
      const token = await loadAnonToken(ref.type, ref.id);
      if (token) {
        // Use current time as export timestamp (latest known state)
        lines.push(`${ref.type}\t${ref.id}\t${token}\t${currentTime}`);
        exported++;
      }
    } catch {
      // Skip entries that fail to load
      continue;
    }
  }

  if (exported === 0) {
    throw new Error("No tokens found to export");
  }

  const blob = new Blob([lines.join("\n") + "\n"], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const date = new Date().toISOString().replace(/[:.]/g, "-").split("T")[0];
  a.href = url;
  a.download = `societea_anon_keys_${date}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return exported;
}

/**
 * Import anonymous keys from a .txt file
 * Supports both v1 (no timestamp) and v2 (with timestamp) formats
 * Only imports tokens that are newer than existing ones (prevents stale overwrites)
 * @param file - The .txt file to import
 * @returns Object with counts of imported, skipped, and failed entries
 */
export async function importKeysFromTxt(
  file: File
): Promise<{ imported: number; skipped: number; errors: number; errorMessages: string[] }> {
  const text = await file.text();
  const lines = text.split(/\r?\n/);
  let imported = 0;
  let skipped = 0;
  let errors = 0;
  const errorMessages: string[] = [];

  const validTypes = ["impact", "comment", "solution", "feedback", "action"];

  for (const raw of lines) {
    const line = raw.trim();
    
    // Skip empty lines and comments
    if (!line || line.startsWith("#")) continue;

    const parts = line.split("\t");
    if (parts.length < 3) {
      errors++;
      errorMessages.push(`Invalid format: ${line.substring(0, 50)}...`);
      continue;
    }

    const [typeStr, idStr, token, timestampStr] = parts;
    const type = typeStr as AnonEntity;

    // Validate entity type
    if (!validTypes.includes(type)) {
      errors++;
      errorMessages.push(`Invalid entity type: ${typeStr}`);
      continue;
    }

    // Validate ID
    if (!idStr || idStr.trim() === "") {
      errors++;
      errorMessages.push(`Invalid ID for ${type}`);
      continue;
    }

    // Validate token
    if (!token || token.trim() === "") {
      errors++;
      errorMessages.push(`Invalid token for ${type}:${idStr}`);
      continue;
    }

    // Parse timestamp (v2 format) or default to 0 (v1 format/legacy)
    const timestamp = timestampStr && /^\d+$/.test(timestampStr) 
      ? Number(timestampStr) 
      : 0;

    // Save token to vault (saveAnonToken will check timestamps)
    try {
      const id = /^\d+$/.test(idStr) ? Number(idStr) : idStr;
      
      // Try to save - it will skip if existing is newer
      const before = await loadAnonToken(type, id);
      await saveAnonToken(type, id, token.trim(), timestamp);
      const after = await loadAnonToken(type, id);
      
      // Check if it was actually saved (not skipped)
      if (before === after && before !== null) {
        skipped++;
      } else {
        imported++;
      }
    } catch (err) {
      errors++;
      errorMessages.push(`Failed to save ${type}:${idStr}`);
    }
  }

  return { imported, skipped, errors, errorMessages };
}

/**
 * Generate a shareable text blob of all keys (for copying to clipboard)
 * @param known - List of known anonymous entity references
 */
export async function generateKeysText(known: KeyRef[]): Promise<string> {
  const lines = [
    "# Societea Anon Keys v2",
    `# origin: ${window.location.hostname}`,
    `# generated_at: ${new Date().toISOString()}`,
    `# total_entries: ${known.length}`,
    `# format: type<TAB>id<TAB>token<TAB>timestamp_ms`,
    "",
  ];

  const currentTime = Date.now();
  
  for (const ref of known) {
    try {
      const token = await loadAnonToken(ref.type, ref.id);
      if (token) {
        lines.push(`${ref.type}\t${ref.id}\t${token}\t${currentTime}`);
      }
    } catch {
      continue;
    }
  }

  return lines.join("\n") + "\n";
}

